import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';
import { NgxPaginationModule } from 'ngx-pagination';
import { IntroductionComponent } from '../../components/introduction/introduction.component';
import { PopupMessageComponent } from '../../components/popup-message/popup-message.component';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { PrvNextButtonsComponent } from '../../components/prv-next-buttons/prv-next-buttons.component';
import { DeleteConfirmationComponent } from '../../components/delete-confirmation/delete-confirmation.component';
import { Icons } from '../../../shared/helpers/icons';
import { IUserInfo } from '../../../shared/models/contact';
import { UserDataService } from '../../../shared/services/userData.service';
import { ISpecialNeedsStudent, ISpecialNeedsStaff } from '../../../shared/models/specialNeeds';
import { SpecialNeedsService } from '../../../shared/services/specialNeeds.service';
import { StudentFormComponent } from './student-form/student-form.component';
import { StaffFormComponent } from './staff-form/staff-form.component';

const STUDENTS = 'Considerations for STUDENTS with Special Needs';
const STAFF = 'Considerations for STAFF with Special Needs';

@Component({
  selector: 'special-needs-consideration',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    NgxPaginationModule,
    IntroductionComponent,
    PopupMessageComponent,
    SpinnerComponent,
    PrvNextButtonsComponent,
    DeleteConfirmationComponent,
    StudentFormComponent,
    StaffFormComponent
  ],
  templateUrl: './special-needs-consideration.component.html',
  styleUrl: './special-needs-consideration.component.css'
})
export class SpecialNeedsConsiderationComponent implements OnInit {

  icons = Icons;
  currentBuildingId: string | null = null;
  currentUser: IUserInfo | null = null;

  tabs = [STUDENTS, STAFF];
  selectedTab: string = STUDENTS;

  studentsList: ISpecialNeedsStudent[] = [];
  staffList: ISpecialNeedsStaff[] = [];

  showStudentForm: boolean = false;
  studentFormItem: ISpecialNeedsStudent | null = null;
  showStaffForm: boolean = false;
  staffFormItem: ISpecialNeedsStaff | null = null;

  showDeleteConfirmation: boolean = false;
  deleteItem: ISpecialNeedsStudent | ISpecialNeedsStaff | null = null;
  deleteKind: 'student' | 'staff' | null = null;

  sortOrder: boolean = true;
  studentPage: number = 1;
  staffPage: number = 1;
  itemsPerPage: number = 10;

  loading: boolean = false;
  messageModal: string | null = null;
  popupMessage: string = '';

  constructor(
    private userDataService: UserDataService,
    private specialNeedsService: SpecialNeedsService
  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getUserData();
    this.loadForTab();
  }

  get isStudentsTab(): boolean { return this.selectedTab === STUDENTS; }
  get isStaffTab(): boolean { return this.selectedTab === STAFF; }

  selectTab(tab: string): void {
    this.selectedTab = tab;
    this.loadForTab();
  }

  loadForTab(): void {
    if (this.isStudentsTab) this.loadStudents();
    else this.loadStaff();
  }

  // --- Students ---
  loadStudents(): void {
    if (!this.currentBuildingId) return;
    this.loading = true;
    this.studentPage = 1;
    this.specialNeedsService.getStudents(this.currentBuildingId).subscribe({
      next: (response) => {
        this.loading = false;
        this.studentsList = response && response.data ? response.data : [];
      },
      error: (error: any) => {
        this.loading = false;
        this.messageModal = 'unsuccess';
        this.popupMessage = error.message;
      }
    });
  }

  sortStudents(key: keyof ISpecialNeedsStudent): void {
    this.sortOrder = !this.sortOrder;
    this.studentsList.sort((a, b) => this.compare(a[key], b[key]));
  }

  openAddStudent(): void { this.studentFormItem = null; this.showStudentForm = true; }
  openEditStudent(item: ISpecialNeedsStudent): void { this.studentFormItem = item; this.showStudentForm = true; }
  closeStudentForm(): void { this.showStudentForm = false; this.studentFormItem = null; }
  handleStudentSaved(message: { type: string, message: string }): void {
    this.closeStudentForm();
    this.loadStudents();
    this.messageModal = message.type;
    this.popupMessage = message.message;
  }

  // --- Staff ---
  loadStaff(): void {
    if (!this.currentBuildingId) return;
    this.loading = true;
    this.staffPage = 1;
    this.specialNeedsService.getStaff(this.currentBuildingId).subscribe({
      next: (response) => {
        this.loading = false;
        this.staffList = response && response.data ? response.data : [];
      },
      error: (error: any) => {
        this.loading = false;
        this.messageModal = 'unsuccess';
        this.popupMessage = error.message;
      }
    });
  }

  sortStaff(key: keyof ISpecialNeedsStaff): void {
    this.sortOrder = !this.sortOrder;
    this.staffList.sort((a, b) => this.compare(a[key], b[key]));
  }

  openAddStaff(): void { this.staffFormItem = null; this.showStaffForm = true; }
  openEditStaff(item: ISpecialNeedsStaff): void { this.staffFormItem = item; this.showStaffForm = true; }
  closeStaffForm(): void { this.showStaffForm = false; this.staffFormItem = null; }
  handleStaffSaved(message: { type: string, message: string }): void {
    this.closeStaffForm();
    this.loadStaff();
    this.messageModal = message.type;
    this.popupMessage = message.message;
  }

  // --- Delete (shared) ---
  openDelete(item: ISpecialNeedsStudent | ISpecialNeedsStaff, kind: 'student' | 'staff'): void {
    this.deleteItem = item;
    this.deleteKind = kind;
    this.showDeleteConfirmation = true;
  }
  closeDelete(): void { this.showDeleteConfirmation = false; this.deleteItem = null; this.deleteKind = null; }
  handleDelete(item: ISpecialNeedsStudent | ISpecialNeedsStaff): void {
    this.showDeleteConfirmation = false;
    const kind = this.deleteKind;
    if (!this.currentBuildingId || !item?.id) { this.closeDelete(); return; }
    this.loading = true;
    const request$ = kind === 'staff'
      ? this.specialNeedsService.deleteStaff(this.currentBuildingId, String(item.id))
      : this.specialNeedsService.deleteStudent(this.currentBuildingId, String(item.id));

    request$.subscribe({
      next: () => {
        this.loading = false;
        this.messageModal = 'success';
        this.popupMessage = 'This occurred after deleting a record.';
        if (kind === 'staff') this.loadStaff(); else this.loadStudents();
        this.deleteItem = null; this.deleteKind = null;
      },
      error: (error: any) => {
        this.loading = false;
        this.messageModal = 'unsuccess';
        this.popupMessage = error.message;
        this.deleteItem = null; this.deleteKind = null;
      }
    });
  }

  private compare(a: any, b: any): number {
    const valueA = a?.toString().toLowerCase() || '';
    const valueB = b?.toString().toLowerCase() || '';
    if (valueA < valueB) return this.sortOrder ? -1 : 1;
    if (valueA > valueB) return this.sortOrder ? 1 : -1;
    return 0;
  }

  canSave(): boolean {
    if (!this.currentUser) return false;
    return this.currentUser.can_edit || this.currentUser.can_admin;
  }

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = '';
  }
}
